// AGC: ManageWarehousesModal
// AGC: Add, edit (rename / location), and archive warehouses.
// AGC: Archive is blocked server-side if warehouse has non-zero balances.

import React, { useState, useCallback } from 'react'
import { X, Plus, Edit2, Archive, MapPin, Check, Loader2, Warehouse } from 'lucide-react'
import type { Warehouse as WarehouseType } from '../../types/inventory'
import type { UseWarehouseReturn } from '../../hooks/useWarehouse'
import toast from 'react-hot-toast'

interface ManageWarehousesModalProps {
  isOpen: boolean
  onClose: () => void
  warehouses: WarehouseType[]
  warehouseHook: UseWarehouseReturn
}

type Mode = 'list' | 'add' | 'edit'

export const ManageWarehousesModal: React.FC<ManageWarehousesModalProps> = ({
  isOpen,
  onClose,
  warehouses,
  warehouseHook,
}) => {
  const [mode, setMode] = useState<Mode>('list')
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseType | null>(null)
  const [name, setName] = useState('')
  const [locationText, setLocationText] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [archivingId, setArchivingId] = useState<string | null>(null)

  const { createWarehouse, updateWarehouse, archiveWarehouse } = warehouseHook

  const openAdd = useCallback(() => {
    setName('')
    setLocationText('')
    setNotes('')
    setEditingWarehouse(null)
    setMode('add')
  }, [])

  const openEdit = useCallback((wh: WarehouseType) => {
    setName(wh.name)
    setLocationText(wh.location?.address || '')
    setNotes(wh.notes || '')
    setEditingWarehouse(wh)
    setMode('edit')
  }, [])

  const handleSave = useCallback(async () => {
    if (!name.trim()) { toast.error('Warehouse name is required'); return }
    setSaving(true)
    try {
      const locationData = locationText.trim() ? { lat: 0, lng: 0, address: locationText.trim() } : undefined
      if (mode === 'add') {
        await createWarehouse({ name: name.trim(), location: locationData, notes: notes.trim() || undefined })
        toast.success('Warehouse created!')
      } else if (editingWarehouse) {
        await updateWarehouse(editingWarehouse.id, { name: name.trim(), location: locationData, notes: notes.trim() || undefined })
        toast.success('Warehouse updated!')
      }
      setMode('list')
    } catch (err: any) {
      toast.error(err.message || 'Failed to save warehouse')
    } finally {
      setSaving(false)
    }
  }, [name, locationText, notes, mode, editingWarehouse, createWarehouse, updateWarehouse])

  const handleArchive = useCallback(async (wh: WarehouseType) => {
    if (!window.confirm(`Archive "${wh.name}"? This cannot be undone if it has stock.`)) return
    setArchivingId(wh.id)
    try {
      await archiveWarehouse(wh.id)
      toast.success('Warehouse archived')
    } catch (err: any) {
      toast.error(err.message || 'Failed to archive warehouse')
    } finally {
      setArchivingId(null)
    }
  }, [archiveWarehouse])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} role="button" aria-label="Close" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClose()} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {mode !== 'list' && (
              <button onClick={() => setMode('list')} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 mr-1">
                ←
              </button>
            )}
            <Warehouse className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-gray-900">
              {mode === 'list' ? 'Manage Warehouses' : mode === 'add' ? 'Add Warehouse' : `Edit: ${editingWarehouse?.name}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* AGC: LIST MODE */}
          {mode === 'list' && (
            <div className="space-y-2">
              {warehouses.map(wh => (
                <div key={wh.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">{wh.name}</span>
                      {wh.isDefault && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">default</span>
                      )}
                    </div>
                    {wh.location?.address && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{wh.location.address}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(wh)}
                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-700 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!wh.isDefault && (
                      <button
                        onClick={() => handleArchive(wh)}
                        disabled={archivingId === wh.id}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Archive"
                      >
                        {archivingId === wh.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Archive className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                onClick={openAdd}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-green-300 text-green-600 hover:bg-green-50 transition-colors font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Warehouse
              </button>
            </div>
          )}

          {/* AGC: ADD / EDIT MODE */}
          {(mode === 'add' || mode === 'edit') && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Main Warehouse, North Storage"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-3.5 h-3.5 inline mr-1" />
                  Location / Address (optional)
                </label>
                <input
                  type="text"
                  value={locationText}
                  onChange={e => setLocationText(e.target.value)}
                  placeholder="e.g. Farm north sector, near Highway 050"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  placeholder="Any additional notes..."
                />
              </div>

              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="w-full py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-green-700 active:ring-4 active:ring-[var(--ring-click-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {mode === 'add' ? 'Create Warehouse' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ManageWarehousesModal
